import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from './client';

/**
 * The signed-in person's own preferences — today, the UI language — stored server-side so the
 * choice follows them between computers (`GET` / `PATCH /my-preferences`; ptc-statetax #54's
 * mechanism, ruled for supervision 2026-10-21). Nobody sets a default: `language` is null until the
 * person chooses with the header toggle.
 */
export interface MyPreferences {
  language: 'en' | 'es' | null;
}

export const MY_PREFERENCES_QUERY_KEY = ['my-preferences'] as const;

export function useMyPreferences(enabled = true) {
  return useQuery<MyPreferences>({
    queryKey: MY_PREFERENCES_QUERY_KEY,
    queryFn: () => fetchApi<MyPreferences>('/my-preferences'),
    enabled,
    // The local cache already painted the page; a slow or failed read just leaves it as it is.
    retry: false,
    staleTime: Infinity,
  });
}

/** Saves the language; a failed save leaves this browser's choice in place, and the next toggle saves again. */
export function useSaveLanguagePreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (language: 'en' | 'es') =>
      fetchApi<MyPreferences>('/my-preferences', { method: 'PATCH', body: JSON.stringify({ language }) }),
    onSuccess: (saved) => queryClient.setQueryData(MY_PREFERENCES_QUERY_KEY, saved),
  });
}
